import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import slugify from "slugify";

import DashboardLayout from "../DashboardLayout";
import ProfileStyle from "../../../assets/stylesheets/dashboard/profile.module.css";

import { getMyFavorites, unlikeFavorite } from "../../../api/user-service/favoriteService";
import { getFarmById } from "../../../api/farm-service/farmService";

function normalizeListResponse(res) {
  const data = res?.data ?? res;
  if (Array.isArray(data)) return { items: data, total: data.length };

  const items = data?.items ?? data?.results ?? data?.rows ?? [];
  const total = data?.total ?? data?.count ?? data?.meta?.total ?? items.length;
  return { items, total };
}

export default function FavoritesPage() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [isMutating, setIsMutating] = useState(false);

  useEffect(() => {
    let alive = true;

    async function load() {
      setIsLoading(true);
      setError("");
      setSelected(new Set());

      try {
        const query = { order: "DESC", sort: "createdAt", limit: 50 };
        const res = await getMyFavorites(query);
        const { items: listItems, total: listTotal } = normalizeListResponse(res);

        // Fetch farm details for each favorite
        const enriched = await Promise.all(
          (Array.isArray(listItems) ? listItems : []).map(async (it) => {
            const farmId = it?.targetId ?? it?.target_id;
            if (!farmId) return it;
            try {
              const farmRes = await getFarmById(farmId);
              const farm = farmRes?.data ?? farmRes;
              return { ...it, _farm: farm };
            } catch {
              return it;
            }
          })
        );

        if (!alive) return;
        setItems(enriched);
        setTotal(listTotal ?? enriched.length);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load");
      } finally {
        if (!alive) return;
        setIsLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  const rows = useMemo(() => {
    return items
      .map((it) => {
        const farmId = it?.targetId ?? it?.target_id ?? it?.target?.id;
        const farm = it?._farm;
        const title = farm?.name ?? it?.title ?? it?.farmTitle ?? it?.targetTitle ?? it?.targetName ?? "";
        const thumb = farm?.coverImage?.viewUrl ?? farm?.coverImage?.url ?? farm?.thumbnailUrl ?? it?.thumbnail_url ?? it?.thumbnailUrl ?? "";

        return {
          key: `favorites-${it?.id ?? farmId ?? ""}-${farmId ?? ""}`,
          id: it?.id ?? farmId, // fallback to farmId if no id
          farmId,
          targetId: farmId,
          title: title || "(Untitled farm)",
          thumb,
        };
      })
      .filter((r) => r.farmId);
  }, [items]);

  const selectedCount = selected.size;

  const toggleSelected = (favoriteId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(favoriteId)) next.delete(favoriteId);
      else next.add(favoriteId);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(rows.map((r) => r.id).filter(Boolean)));
  };

  const clearSelection = () => setSelected(new Set());

  const handleRemoveSelected = async () => {
    if (selectedCount === 0) return;

    setIsMutating(true);
    try {
      const selectedIds = Array.from(selected);

      const targets = rows
        .filter((r) => selectedIds.includes(r.id))
        .map((r) => r.targetId)
        .filter(Boolean);

      await Promise.all(targets.map((id) => unlikeFavorite("FARM", id)));

      setItems((prev) =>
        prev.filter((it) => {
          const farmId = it?.targetId ?? it?.target_id;
          return !targets.includes(farmId);
        })
      );
      clearSelection();
    } finally {
      setIsMutating(false);
    }
  };

  const handleRowClick = async (row) => {
    if (!row?.farmId) return;

    let title = row.title;

    // If title is missing/placeholder, fetch farm to build a stable slug.
    if (!title || title === "(Untitled farm)") {
      try {
        const res = await getFarmById(row.farmId);
        const farm = res?.data ?? res;
        title = farm?.name || title;
      } catch {
        // ignore
      }
    }

    const slug = slugify(title || "farm", { lower: true, strict: true });
    navigate(`/farm/${row.farmId}/${slug}`);
  };

  return (
    <DashboardLayout>
      <div className={ProfileStyle["section"]}>
        <div className={ProfileStyle["headerRow"]}>
          <div>
            <h2 className={ProfileStyle["title"]}>My Favorites</h2>
            <p className={ProfileStyle["subTitle"]}>Favorite farms</p>
          </div>
          <div className={ProfileStyle["countBadge"]} title="Total">
            {total || rows.length}
          </div>
        </div>

        <div className={ProfileStyle["card"]}>
          <div className={ProfileStyle["toolbar"]}>
            <div className={ProfileStyle["toolbarLeft"]}>
              <button
                type="button"
                className={ProfileStyle["actionBtn"]}
                onClick={selectAll}
                disabled={rows.length === 0}
              >
                Select all
              </button>
              <button
                type="button"
                className={ProfileStyle["actionBtn"]}
                onClick={clearSelection}
                disabled={selectedCount === 0}
              >
                Clear
              </button>
              <span className={ProfileStyle["smallText"]}>
                Selected: <b>{selectedCount}</b>
              </span>
            </div>

            <button
              type="button"
              className={ProfileStyle["actionBtn"]}
              onClick={handleRemoveSelected}
              disabled={selectedCount === 0 || isMutating}
              title="Remove from favorites"
            >
              {isMutating ? "Removing..." : "Remove favorites"}
            </button>
          </div>

          {isLoading ? (
            <div className={ProfileStyle["empty"]}>Loading...</div>
          ) : error ? (
            <div className={ProfileStyle["empty"]}>{error}</div>
          ) : rows.length === 0 ? (
            <div className={ProfileStyle["empty"]}>There are no favorites.</div>
          ) : (
            <div className={ProfileStyle["list"]}>
              {rows.map((row) => {
                const checked = selected.has(row.id);

                return (
                  <div
                    key={row.key}
                    className={ProfileStyle["row"]}
                    onClick={() => handleRowClick(row)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRowClick(row);
                    }}
                  >
                    <input
                      type="checkbox"
                      className={ProfileStyle["checkbox"]}
                      checked={checked}
                      onChange={() => toggleSelected(row.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${row.title}`}
                    />

                    <div className={ProfileStyle["thumb"]} aria-hidden="true">
                      {row.thumb ? <img src={row.thumb} alt="" /> : null}
                    </div>

                    <div>
                      <p className={ProfileStyle["name"]}>{row.title}</p>
                    </div>

                    <div className={ProfileStyle["right"]} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
